import { useQuery } from "@tanstack/react-query";
import { Sparkles, TrendingUp, Loader2, User } from "lucide-react";
import api from "@/lib/api";

const ORIENTATION_COLORS = {
  Gratitude: "#D98E73", Awareness: "#144F75", Unity: "#5C8A72", Growth: "#B5794E", Energy: "#8A6FB0",
};

export default function GaugeMatchesPanel() {
  const q = useQuery({ queryKey: ["admin-gauge-matches"], queryFn: () => api.get("/admin/gauge/matches").then((r) => r.data) });
  const data = q.data;
  const byOrientation = data?.by_orientation || {};
  const orientationTotal = Object.values(byOrientation).reduce((a, b) => a + b, 0) || 1;

  return (
    <div data-testid="admin-gauge-matches-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-[#1A242B]">GAUGE match analytics</h2>
        <p className="text-sm text-[#4A5D6B]">Warm leads from the exploration gateway — who's being matched to which Enhancers, and by orientation.</p>
      </div>

      {q.isLoading && <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading matches…</p>}

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="eyw-card p-6" data-testid="gauge-total-matches">
              <div className="text-3xl font-serif text-[#144F75]">{data.total}</div>
              <p className="text-sm text-[#4A5D6B] mt-1 flex items-center gap-1.5"><Sparkles size={14} /> Total matches</p>
            </div>
            <div className="eyw-card p-6">
              <div className="text-3xl font-serif text-[#5C8A72]">{data.by_enhancer?.length || 0}</div>
              <p className="text-sm text-[#4A5D6B] mt-1 flex items-center gap-1.5"><User size={14} /> Enhancers matched</p>
            </div>
            <div className="eyw-card p-6">
              <div className="text-3xl font-serif text-[#D98E73]">{Object.keys(byOrientation).length}</div>
              <p className="text-sm text-[#4A5D6B] mt-1 flex items-center gap-1.5"><TrendingUp size={14} /> Orientations</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Warm leads by enhancer */}
            <div data-testid="gauge-by-enhancer">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Warm leads by Enhancer</h3>
              <div className="eyw-card divide-y divide-[#E5E0D8]">
                {(data.by_enhancer || []).map((e) => (
                  <div key={e.enhancer_id} className="p-4 flex items-center justify-between">
                    <span className="text-sm text-[#1A242B]">{e.enhancer_name || "Unnamed Enhancer"}</span>
                    <span className="text-sm font-semibold text-[#144F75]">{e.count} {e.count === 1 ? "lead" : "leads"}</span>
                  </div>
                ))}
                {data.by_enhancer?.length === 0 && <p className="text-sm text-[#4A5D6B] p-4">No matches logged yet.</p>}
              </div>
            </div>

            {/* Orientation breakdown */}
            <div data-testid="gauge-by-orientation">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">By primary orientation</h3>
              <div className="eyw-card p-5 space-y-3">
                {Object.entries(byOrientation).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                  <div key={name}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#1A242B]">{name || "—"}</span>
                      <span className="text-[#4A5D6B]">{count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#E5E0D8] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(count / orientationTotal) * 100}%`, background: ORIENTATION_COLORS[name] || "#144F75" }} />
                    </div>
                  </div>
                ))}
                {Object.keys(byOrientation).length === 0 && <p className="text-sm text-[#4A5D6B]">No orientation data yet.</p>}
              </div>
            </div>
          </div>

          {/* Recent matches */}
          <div className="mt-8" data-testid="gauge-recent-matches">
            <h3 className="font-serif text-lg text-[#1A242B] mb-3">Recent matches</h3>
            <div className="eyw-card divide-y divide-[#E5E0D8]">
              {(data.recent || []).map((m) => (
                <div key={m.id} className="p-4 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-[#1A242B]">{m.enhancer_name || "—"}</div>
                    <div className="text-xs text-[#4A5D6B]">{m.primary_orientation} · {m.path_title || m.path_slug || "—"}{m.guest ? " · guest" : m.user_email ? ` · ${m.user_email}` : ""}</div>
                  </div>
                  <div className="text-xs text-[#8FA89B]">{m.match_score ? `${m.match_score}% fit` : ""}{m.created_at ? ` · ${String(m.created_at).slice(0, 10)}` : ""}</div>
                </div>
              ))}
              {data.recent?.length === 0 && <p className="text-sm text-[#4A5D6B] p-4">No matches yet — they appear as participants complete the GAUGE exploration.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
